/**
 * cas-5070: a locale-neutral key for the top-level <NuxtPage>.
 *
 * With `strategy: 'prefix_except_default'`, /pricing and /es/pricing are two route
 * records. Nuxt keys a page by its matched route path, so an automatic locale switch
 * (the account-locale plugin restoring a saved language after sign-in, or the layout
 * restoring the browser's saved language) changed the key and REMOUNTED the page about
 * 2 s after load: early clicks were dropped and the page re-resolved its state (trace in
 * cas-5070/LEDGER.md). This key is Nuxt's default (the top-level matched path with its
 * params filled in) minus the locale prefix, so those switches update the page in place.
 *
 * An explicit language pick (the footer) still rebuilds the page, as before: it bumps
 * `explicitLocaleSwitches`, which is part of the key.
 */
import { ref } from 'vue';
import { stripLocalePrefix } from '~/utils/public-audit-path';

export const explicitLocaleSwitches = ref(0);

type KeyRoute = {
  path: string;
  params: Record<string, unknown>;
  matched: ReadonlyArray<{ path: string }>;
};

/** Nuxt's interpolatePath: `/i/:itemId()` + params → `/i/abc`. */
function interpolate(pattern: string, params: Record<string, unknown>): string {
  return pattern
    .replace(/(:\w+)\([^)]*\)/g, '$1')
    .replace(/(:\w+)[?+*]/g, '$1')
    .replace(/:\w+/g, name => {
      const value = params[name.slice(1)];
      return Array.isArray(value) ? value.join('/') : value == null ? '' : String(value);
    });
}

export function localeNeutralPageKey(route: KeyRoute): string {
  const top = route.matched[0]?.path ?? route.path;
  const key = stripLocalePrefix(interpolate(top, route.params)) || '/';
  return explicitLocaleSwitches.value ? `${key}#locale-${explicitLocaleSwitches.value}` : key;
}
